/**
 * The tab shell's navigation rules, ported from Android's
 * `ui/navigation/TabNavigation.kt` and kept pure so they can be asserted without
 * rendering anything. `MainShell` only calls these; it decides nothing itself.
 *
 * **Android's `handlesBack` / `backTarget` are deliberately absent.** They exist
 * to answer "what should the system Back button do inside the shell", and a
 * WebClip has no Back button: it opens without Safari's chrome, and the only
 * gesture that leaves it leaves the app entirely. Porting them would add two
 * exported rules that no caller can ever reach, and the next reader would go
 * looking for the button that triggers them.
 *
 * Android's `Routes.KEY` and `Routes.MAIN` are absent for a related reason:
 * there is no navigator here. `App.tsx` shows the key-entry gate or the shell
 * based on whether a device key exists, so the gate is a state, not a route.
 * The rule that the gate is *not a tab* still matters and is still asserted —
 * if it ever became one, the bottom bar would show on the sign-in screen.
 */

/** Tabs in bar order. The first one is where the shell starts. */
export const routes = ['home', 'result', 'settings'] as const;

/** The three tabs, derived from the list so the two can never drift apart. */
export type TabRoute = (typeof routes)[number];

/** The shell's start destination — the dashboard, as on Android. */
export const startRoute: TabRoute = routes[0];

/** Whether a route names one of the tabs. Anything else is not part of the shell. */
export function isTab(route: string | null | undefined): route is TabRoute {
  return typeof route === 'string' && (routes as readonly string[]).includes(route);
}

/**
 * Whether tapping [target] should actually switch tabs.
 *
 * Re-selecting the tab you are already on must be a no-op. On Android that
 * protected the tab's saved scroll state; here it also protects live state that
 * a re-mount would throw away — the Results filter, and the scroll position of a
 * Home screen whose progress module is updating while you look at it.
 */
export function shouldNavigate(currentRoute: string | null | undefined, target: string): boolean {
  return isTab(target) && currentRoute !== target;
}
